/**
 * Subdomain → realm resolver.
 *
 * The same React bundle is served to three product realms:
 *   • Spares (default)     — admin.crystalgroup.in / localhost / anything else
 *   • IMS Containers       — ims.crystalgroup.in / containers.*    (legacy slot)
 *   • Procurement Portal   — purchase.crystalgrp.xyz   (new; note `crystalgrp.xyz`)
 *
 * The realm is decided by hostname. Routes still live under their own URL
 * prefix (/ims/*, /procurement/*) so a Spares admin can also dev against
 * those paths on localhost; subdomain-based redirects only bias the
 * landing page so end-users on the procurement subdomain land at
 * /procurement instead of the Spares dashboard.
 */
export type Realm = 'spares' | 'ims' | 'procurement' | 'porta';

export function detectRealmFromHostname(hostname: string = (typeof window !== 'undefined' ? window.location.hostname : '')): Realm {
  const h = String(hostname || '').toLowerCase();
  // Porta Cabin subdomain — porta.crystalgrp.xyz (its own realm + login).
  if (h.startsWith('porta.') || h.includes('.porta.')) return 'porta';
  // Procurement subdomain — exact match for production + "*.purchase.*"
  // catches any future preview hosts (e.g. purchase-staging, purchase.dev).
  if (h.startsWith('purchase.') || h.includes('.purchase.')) return 'procurement';
  // IMS Containers historically lives at an `ims.` subdomain; this is left
  // here for symmetry with the procurement realm even though the current
  // production deploy doesn't yet split IMS off by host (IMS users hit the
  // same host and rely on the imsOnly account flag to bounce them).
  if (h.startsWith('ims.') || h.startsWith('containers.')) return 'ims';
  return 'spares';
}

/** Default landing path for a realm — used after login and as a fallback
 *  when an authed user with the wrong realm hits the root. */
export function realmHome(realm: Realm): string {
  switch (realm) {
    case 'porta':       return '/porta';
    case 'procurement': return '/procurement';
    case 'ims':         return '/ims/overview';
    case 'spares':
    default:            return '/';
  }
}

/** Hostname → "what kind of app are we presenting" — for the Login page
 *  title, browser tab name, and post-login redirect target. */
export function currentRealm(): Realm {
  return detectRealmFromHostname();
}

/** Realm-aware porta-cabin route prefixes. The same PortaCabin* components are
 *  mounted under BOTH /porta/* (porta realm) and /procurement/porta-cabin/*
 *  (purchase realm) — this returns the right paths for whichever they render in
 *  so navigation never bounces a user across realms. */
export function portaPaths() {
  const onPorta = typeof window !== 'undefined' && window.location.pathname.startsWith('/porta');
  return onPorta
    ? { list: '/porta/list', create: '/porta/new', build: (id: string) => `/porta/${encodeURIComponent(id)}/build`, order: (id: string) => `/porta/${encodeURIComponent(id)}/order` }
    : { list: '/procurement/porta-cabin', create: '/procurement/porta-cabin/new', build: (id: string) => `/procurement/porta-cabin/${encodeURIComponent(id)}/build`, order: (id: string) => `/procurement/porta-cabin/${encodeURIComponent(id)}/order` };
}
